import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dhfApi } from '../../utils/dhfApi';
import { Modal } from '../review/reviewUi';
import { ScoreRing, healthColor, utc } from './dhfUi';
import '../../styles/DocReview.css';
import '../../styles/Risk.css';
import '../../styles/Dhf.css';

/**
 * DHF home (mockup screen 1, list): one card per DHF. A DHF is a Traceability panel linked
 * here (design D1: "select a trace matrix and the system does the rest") — it stays a trace
 * panel; Remove only unlinks it.
 */
export default function DhfHome({ canManagePanels, hasTrace }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [picker, setPicker] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      setData(await dhfApi.panels());
      setError('');
    } catch (e) {
      setError(e.message);
      setData((d) => d || { linked: [], candidates: [] });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (id) => {
    try {
      await dhfApi.link(id);
      setPicker(false);
      navigate(`/dhf/${id}`);
    } catch (e) {
      setError(e.message);
    }
  };

  const remove = async (p) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove “${p.name}” from the DHF? The Traceability panel itself is kept.`)) return;
    try {
      await dhfApi.unlink(p.id);
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const levelNames = (p) => {
    const levels = Array.isArray(p.levels) ? p.levels : [];
    return [p.base_level_name || 'L1', ...levels.map((l, i) => l.level_name || `L${i + 2}`)].join(' → ');
  };

  return (
    <div className="drv-page">
      <div className="drv-head">
        <div>
          <h1 className="drv-title">Design History File</h1>
          <p className="drv-muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
            Pick a Traceability panel — every document that holds its items is scored for completion,
            coverage and verification, and opens like a document.
          </p>
        </div>
        {canManagePanels && hasTrace && (
          <button type="button" className="drv-btn drv-btn--primary drv-btn--lg" onClick={() => setPicker(true)}>
            + New DHF from a Traceability panel
          </button>
        )}
      </div>

      {error && <div className="drv-error" role="alert">{error}</div>}
      {data === null && <div className="drv-muted">Loading…</div>}
      {data && !data.trace_allowed && (
        <div className="drv-empty">A DHF is built from a Traceability panel, and this account has no Traceability licence.</div>
      )}
      {data?.trace_allowed && data.linked.length === 0 && (
        <div className="drv-empty">
          No DHF yet.{' '}
          {canManagePanels
            ? <>Add one with <strong>+ New DHF from a Traceability panel</strong>.</>
            : 'Ask someone who can create panels to add one.'}
        </div>
      )}

      <div className="dhf-cards">
        {(data?.linked || []).map((p) => {
          const d = p.dhf;
          return (
            <section key={p.id} className="drv-card dhf-card">
              <div className="dhf-card-top">
                <ScoreRing health={d?.overall?.health} />
                <div>
                  <h2>{p.name}</h2>
                  <p>{levelNames(p)}</p>
                  <p>{d ? `${d.n_docs} documents · ${d.n_items} items · ${d.pool} in the pool · built ${utc(d.built_at)}`
                    : 'Not built yet'}</p>
                </div>
              </div>
              {d && d.docs.length > 0 && (
                <ul className="dhf-card-docs">
                  {d.docs.map((x) => (
                    <li key={x.id}><span>{x.name}</span><b style={{ color: healthColor(x.health) }}>{x.health ?? '—'}</b></li>
                  ))}
                </ul>
              )}
              {d?.warnings > 0 && <div className="drv-muted" style={{ fontSize: 12 }}>⚠ {d.warnings} thing(s) to check — open it to see</div>}
              <div className="dhf-card-foot">
                {canManagePanels && (
                  <button type="button" className="drv-btn" onClick={() => remove(p)} title="Remove from the DHF (the panel is kept)">Remove</button>
                )}
                <button type="button" className="drv-btn drv-btn--primary" onClick={() => navigate(`/dhf/${p.id}`)}>Open</button>
              </div>
            </section>
          );
        })}
      </div>

      {picker && (
        <Modal
          title="New DHF from a Traceability panel"
          subtitle="Its items are grouped by the document they sit in (Root Document Id). The panel stays in Traceability."
          onClose={() => setPicker(false)}
          footer={<button type="button" className="drv-btn drv-btn--lg" onClick={() => setPicker(false)}>Close</button>}
        >
          {(data?.candidates || []).length === 0 ? (
            <div className="drv-muted">Every Traceability panel you can see is already a DHF.</div>
          ) : (
            <ul className="rsk-trace-pick">
              {[...data.candidates].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((p) => (
                <li key={p.id}>
                  <div>
                    <strong>{p.name}</strong>
                    <span>{levelNames(p)}</span>
                  </div>
                  <button type="button" className="drv-btn drv-btn--primary" onClick={() => add(p.id)}>Add</button>
                </li>
              ))}
            </ul>
          )}
        </Modal>
      )}
    </div>
  );
}
